import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { catalogoTelasWeb } from '../components/Layout'

/* Lançador de módulos. Os ícones são os PNGs 3D de src/assets/modulos —
   normalizados fora do código (256×256, fundo transparente, mesma massa visual),
   então aqui basta uma caixa de lado fixo: ver `.modulo-icon` em index.css.

   Só quatro módulos existem de fato hoje (os que têm `path`). Os outros ficam
   na grade de propósito — dão a ver o sistema inteiro —, marcados com a pílula
   "Em breve"; clicar num deles abre o aviso em modal. */
import iconeAcertoViagens from '../assets/modulos/acerto-viagens.png'
import iconeCombustiveis from '../assets/modulos/combustiveis.png'
import iconeComponentes from '../assets/modulos/componentes.png'
import iconeCompras from '../assets/modulos/compras.png'
import iconeCadastroUsuarios from '../assets/modulos/cadastro-usuarios.png'
import iconeEstoque from '../assets/modulos/estoque.png'
import iconeFaturamento from '../assets/modulos/faturamento.png'
import iconeFinanceiro from '../assets/modulos/financeiro.png'
import iconeFretamento from '../assets/modulos/fretamento.png'
import iconeFuncionarios from '../assets/modulos/funcionarios.png'
import iconeInfoGeral from '../assets/modulos/info-geral.png'
import iconeIntegContabil from '../assets/modulos/integ-contabil.png'
import iconeLivrosFiscais from '../assets/modulos/livros-fiscais.png'
import iconeLocacao from '../assets/modulos/locacao.png'
import iconeLogs from '../assets/modulos/logs.png'
import iconeManuais from '../assets/modulos/manuais.png'
import iconeMecanica from '../assets/modulos/mecanica.png'
import iconePassagens from '../assets/modulos/passagens.png'
import iconePedagio from '../assets/modulos/pedagio.png'
import iconePneus from '../assets/modulos/pneus.png'
import iconePreventiva from '../assets/modulos/preventiva.png'
import iconeRateios from '../assets/modulos/rateios.png'
import iconeSac from '../assets/modulos/sac.png'
import iconeTabelasGerais from '../assets/modulos/tabelas-gerais.png'
import iconeTrafego from '../assets/modulos/trafego.png'
import iconeTurismo from '../assets/modulos/turismo.png'
import iconeVeiculos from '../assets/modulos/veiculos.png'
import iconeVendas from '../assets/modulos/vendas.png'

interface Modulo {
  label: string
  icon: string
  path?: string
  // Oculto do lançador por pedido do usuário (09-21): só Vendas/Compras/Combustíveis
  // ficam visíveis por enquanto — os outros voltam conforme forem liberados, um a um.
  oculto?: boolean
}

interface Bloco {
  titulo: string
  modulos: Modulo[]
}

/* Vinte e oito módulos numa grade lisa viram parede. Os blocos são só leitura —
   não têm efeito em permissão nem em rota. */
const BLOCOS: Bloco[] = [
  {
    titulo: 'Operação',
    modulos: [
      { label: 'Veículos', icon: iconeVeiculos, oculto: true },
      { label: 'Funcionários', icon: iconeFuncionarios, path: '/funcionarios/cadastro' },
      { label: 'Fretamento', icon: iconeFretamento, oculto: true },
      { label: 'Turismo', icon: iconeTurismo, oculto: true },
      { label: 'Tráfego', icon: iconeTrafego, oculto: true },
      { label: 'Passagens', icon: iconePassagens, oculto: true },
      { label: 'Locação', icon: iconeLocacao, oculto: true },
      { label: 'Pedágio', icon: iconePedagio, oculto: true },
    ],
  },
  {
    titulo: 'Manutenção',
    modulos: [
      { label: 'Mecânica', icon: iconeMecanica, oculto: true },
      { label: 'Preventivas', icon: iconePreventiva, oculto: true },
      { label: 'Pneus', icon: iconePneus, oculto: true },
      { label: 'Componentes', icon: iconeComponentes, oculto: true },
      { label: 'Combustíveis', icon: iconeCombustiveis, path: '/combustiveis' },
    ],
  },
  {
    titulo: 'Estoque',
    modulos: [
      { label: 'Estoque', icon: iconeEstoque, oculto: true },
    ],
  },
  {
    titulo: 'Financeiro',
    modulos: [
      { label: 'Financeiro', icon: iconeFinanceiro, oculto: true },
      { label: 'Faturamento', icon: iconeFaturamento, oculto: true },
      { label: 'Acerto de viagens', icon: iconeAcertoViagens, oculto: true },
      { label: 'Rateios', icon: iconeRateios, oculto: true },
    ],
  },
  {
    titulo: 'Fiscal e Contábil',
    modulos: [
      { label: 'Compras', icon: iconeCompras, path: '/compras' },
      { label: 'Vendas', icon: iconeVendas, path: '/cte/lista' },
      { label: 'Livros fiscais', icon: iconeLivrosFiscais, oculto: true },
      { label: 'Integração contábil', icon: iconeIntegContabil, oculto: true },
    ],
  },
  {
    titulo: 'Administração',
    modulos: [
      { label: 'Cadastro de usuários', icon: iconeCadastroUsuarios, path: '/administracao/usuarios' },
      { label: 'Tabelas Gerais', icon: iconeTabelasGerais, oculto: true },
      { label: 'Informações Gerais', icon: iconeInfoGeral, oculto: true },
    ],
  },
  {
    titulo: 'Atendimento e Suporte',
    modulos: [
      { label: 'SAC', icon: iconeSac, oculto: true },
      { label: 'Manuais', icon: iconeManuais, oculto: true },
    ],
  },
  {
    titulo: 'Sistema',
    modulos: [
      { label: 'Logs', icon: iconeLogs, path: '/sistema/log' },
    ],
  },
]

interface Props {
  acessoLimitado: boolean
  apenasVendas: boolean
  telasPermitidas: Set<string> | null
}

// Módulo do lançador → módulo do catálogo da aba Opções (o nome difere só aqui)
const MODULO_CATALOGO: Record<string, string> = { 'Cadastro de usuários': 'Administração' }

export default function Principal({ acessoLimitado, apenasVendas, telasPermitidas }: Props) {
  const navigate = useNavigate()
  const [aviso, setAviso] = useState<Modulo | null>(null)

  // Com a aba Opções configurada: telas liberadas de cada módulo, na ordem do menu
  const telasDoModulo = (m: Modulo): string[] | null => {
    if (!telasPermitidas) return null
    const nome = MODULO_CATALOGO[m.label] ?? m.label
    const doCatalogo = catalogoTelasWeb().filter(g => g.modulo === nome).flatMap(g => g.telas.map(t => t.rota))
    return doCatalogo.length ? doCatalogo.filter(r => telasPermitidas.has(r)) : null // fora do catálogo: não filtra
  }

  /* Permissão filtra por módulo, não por bloco: bloco que ficou sem módulo nenhum não aparece.
     Aba Opções configurada manda; senão vale o tiplib. */
  const permitido = (m: Modulo) => {
    const telas = telasDoModulo(m)
    if (telas) return telas.length > 0
    if (telasPermitidas) return true
    return apenasVendas ? m.label === 'Vendas'
      : acessoLimitado ? m.label === 'Vendas' || m.label === 'Compras'
      : true
  }

  // Entra pela tela padrão do módulo; se ela não estiver liberada, pela primeira liberada
  const destino = (m: Modulo) => {
    const telas = telasDoModulo(m)
    return !telas || !m.path || telas.includes(m.path) ? m.path : telas[0]
  }

  const blocosVisiveis = BLOCOS
    .map(b => ({ ...b, modulos: b.modulos.filter(m => !m.oculto && permitido(m)) }))
    .filter(b => b.modulos.length > 0)

  const emBreve = (m: Modulo) => !m.path

  return (
    <div>
      <div className="card mb-5">
        <h2 className="mb-1 text-xl font-bold text-brand-blue">Bem-vindo ao FrotaWeb</h2>
        <p className="text-sm text-ink-muted">Selecione um módulo abaixo para começar.</p>
      </div>

      <div className="flex flex-col gap-6">
        {blocosVisiveis.map(bloco => (
          <section key={bloco.titulo}>
            <div className="mb-2.5 flex items-center gap-3">
              <h3 className="text-[0.68rem] font-bold uppercase tracking-[0.08em] text-ink-subtle">
                {bloco.titulo}
              </h3>
              <span className="h-px flex-1 bg-edge-muted" />
            </div>

            <div className="modulo-grid">
              {bloco.modulos.map(m => (
                <button
                  key={m.label}
                  className={`modulo-tile${emBreve(m) ? ' modulo-tile--em-breve' : ''}`}
                  onClick={() => (m.path ? navigate(destino(m)!) : setAviso(m))}
                  title={emBreve(m) ? `${m.label} (em breve)` : m.label}
                >
                  <span className="modulo-icon">
                    <img src={m.icon} alt="" />
                  </span>
                  <span className="modulo-label">{m.label}</span>
                  {emBreve(m) && <span className="modulo-badge">Em breve</span>}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {aviso && <ModalEmBreve modulo={aviso} onClose={() => setAviso(null)} />}
    </div>
  )
}

/* Aviso de módulo que ainda não existe. Usa a camada compartilhada de modal
   (`.modal-*` em index.css): véu escuro com desfoque, cabeçalho azul da marca,
   botão de fechar laranja. */
function ModalEmBreve({ modulo, onClose }: { modulo: Modulo; onClose: () => void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box !max-w-[420px]" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{modulo.label}</h2>
          <button className="modal-fechar" onClick={onClose}>✕</button>
        </div>

        <div className="modal-corpo flex flex-col items-center gap-3 text-center">
          <span className="modulo-icon">
            <img src={modulo.icon} alt="" />
          </span>
          <span className="modulo-badge !static">Em breve</span>
          <p className="text-sm text-ink-muted">
            O módulo <strong className="font-semibold text-ink">{modulo.label}</strong> está
            em desenvolvimento e chega numa próxima versão do FrotaWeb.
          </p>
        </div>

        <div className="modal-footer">
          <button className="btn btn-orange" onClick={onClose}>Entendi</button>
        </div>
      </div>
    </div>
  )
}